import { session } from '../lib/api'
import { useApi, useConnection, useOutboxCount } from '../lib/hooks'
import { clock, dateTime, initials } from '../lib/format'
import { Banner, Panel, Pill, StatMini } from '../ui/kit'
import { FieldHeader } from './FieldShell'

interface Bundle {
  generatedAt: string
  scales: Array<{ id: string; code: string; certNumber: string | null; certExpiry: string | null; driftFlag: boolean }>
  policy: { varianceTolerancePct: number; shrinkTargetPct: number; farmerPayoutCutoff: string; currency: string }
}

export function More({ onSignOut }: { onSignOut: () => void }) {
  const user = session.user
  const { online } = useConnection()
  const queued = useOutboxCount()
  const bundle = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })

  const theme = localStorage.getItem('ledger.theme') ?? 'light'

  function cycleTheme() {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    localStorage.setItem('ledger.theme', next)
    if (next === 'system') document.documentElement.removeAttribute('data-theme')
    else document.documentElement.setAttribute('data-theme', next)
    window.location.reload()
  }

  return (
    <>
      <FieldHeader />
      <div className="field-greet">
        <h1>More</h1>
        <p>Your device, your scales, and the rules this route runs under.</p>
      </div>

      <div className="field-body">
        <Panel title="Signed in as">
          <div className="panel-body row" style={{ gap: 12 }}>
            <span className="avatar" style={{ width: 44, height: 44, fontSize: 'var(--fs-md)' }}>
              {initials(user?.fullName)}
            </span>
            <div className="grow">
              <b style={{ fontSize: 'var(--fs-lg)' }}>{user?.fullName}</b>
              <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                {user?.roles.join(' · ').replace(/_/g, ' ')} · {user?.org.name}
              </div>
            </div>
          </div>
        </Panel>

        <div className="mini-row">
          <StatMini label="Connection" value={online ? 'Online' : 'Offline'} />
          <StatMini label="Queued" value={queued} note={queued > 0 ? 'will send itself' : 'all clear'} />
          <StatMini
            label="Reference cached"
            value={bundle.data ? 'Yes' : 'No'}
            note={bundle.data ? dateTime(bundle.data.generatedAt) : 'sign in online once'}
          />
        </div>

        {bundle.data && (
          <>
            <Panel title="The rules you work to" meta="set by the office — this phone cannot change them">
              <div className="panel-body col" style={{ gap: 8 }}>
                <Rule label="Weight difference allowed" value={`${bundle.data.policy.varianceTolerancePct}%`} />
                <Rule label="Weight lost, target" value={`under ${bundle.data.policy.shrinkTargetPct}%`} />
                <Rule label="Farmers paid by" value={clock(bundle.data.policy.farmerPayoutCutoff)} />
                <Rule label="Currency" value={bundle.data.policy.currency} />
              </div>
            </Panel>

            <Panel title="Scales">
              <div className="panel-body col" style={{ gap: 10 }}>
                {bundle.data.scales.map((s) => (
                  <div key={s.id} className="spread">
                    <span>
                      <b className="mono">{s.code}</b>
                      <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                        {s.certNumber ? `cert ${s.certNumber}` : 'no certificate on file'}
                      </div>
                    </span>
                    {s.driftFlag ? <Pill tone="crit">Drift</Pill> : <Pill tone="ok">Good</Pill>}
                  </div>
                ))}
              </div>
            </Panel>
          </>
        )}

        <button className="btn ghost block" onClick={cycleTheme}>
          Theme: {theme}
        </button>

        {queued > 0 && (
          <Banner tone="warn" title={`${queued} item(s) still on this device`}>
            Get a signal before you hand the phone over or sign out. Nobody else can send them.
          </Banner>
        )}

        <button className="btn danger block" onClick={onSignOut}>Sign out</button>
      </div>
    </>
  )
}

function Rule({ label, value }: { label: string; value: string }) {
  return (
    <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
      <span className="muted">{label}</span>
      <b className="num">{value}</b>
    </div>
  )
}
